import { CyclesInterface } from 'types/user';

import { nomeDoMes } from './minha-equipe-calculos';

/**
 * Calendário do ciclo vigente a partir de `cycles` do AuthContext (`find/calendar_v2`, um item por mês)
 * e do horário do servidor. Datas em milissegundos; dias e meses lidos no horário de Brasília (UTC-3).
 */

export type TipoDeEtapa = 'cad' | 'val' | 'n1' | 'n2' | 'pit' | 'n3';

export interface Etapa {
  tipo: TipoDeEtapa;
  inicio: number;
  fim: number;
  /** Mês, trimestre ou semestre a que a etapa se refere. */
  referencia: string;
}

export interface EtapaNoCalendario extends Etapa {
  nome: string;
  cor: string;
  /** "dd/mm a dd/mm". */
  periodo: string;
  agora: boolean;
  encerrada: boolean;
}

export interface MesDoCalendario {
  /** "AAAA-MM" do início das etapas do grupo. */
  chave: string;
  nome: string;
  ano: number;
  etapas: EtapaNoCalendario[];
}

/** Campos do calendar_v2 que o tipo compartilhado não declara. */
type MesDoCalendarV2 = CyclesInterface & {
  trimester?: { title?: string };
  cycle: CyclesInterface['cycle'] & { title_semester?: string };
};

const LEGENDA_POR_TIPO: Record<TipoDeEtapa, { nome: string; cor: string }> = {
  cad: { nome: 'Cadastramento', cor: 'bg-brand-blue' },
  val: { nome: 'Validação', cor: 'bg-brand-green' },
  n1: { nome: 'Apreciação Nível 1', cor: 'bg-brand-yellow' },
  n2: { nome: 'Apreciação Nível 2', cor: 'bg-brand-purple-light' },
  pit: { nome: 'Apreciação Pit Stop', cor: 'bg-surface-300' },
  n3: { nome: 'Apreciação Nível 3', cor: 'bg-brand-pink-light' },
};

const ORDEM_DOS_TIPOS = Object.keys(LEGENDA_POR_TIPO) as TipoDeEtapa[];
export const LEGENDA_DO_CALENDARIO = ORDEM_DOS_TIPOS.map(tipo => ({ tipo, ...LEGENDA_POR_TIPO[tipo] }));

/** Item de legenda: nome e cor de um tipo de etapa. Outras dinâmicas (Ações ECOA) passam a própria legenda. */
export interface ItemDaLegenda {
  tipo: TipoDeEtapa;
  nome: string;
  cor: string;
}

const MESES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
const FUSO_DE_BRASILIA_MS = 3 * 60 * 60 * 1000;

/** Data em Brasília lida pelos getters UTC. */
export function emBrasilia(ms: number): Date {
  return new Date(ms - FUSO_DE_BRASILIA_MS);
}

function diaEMes(ms: number): string {
  const data = emBrasilia(ms);
  return `${String(data.getUTCDate()).padStart(2, '0')}/${String(data.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** "Nível 2 - 3° Trimestre" → "3º Trimestre". */
function semPrefixoDeNivel(titulo: string): string {
  return titulo.replace(/^Nível \d+ - /, '').replace('°', 'º');
}

function referenciaDoTrimestre(mes: MesDoCalendarV2): string {
  if (mes.trimester?.title) return semPrefixoDeNivel(mes.trimester.title);
  return `${Math.ceil((emBrasilia(mes.date_trimester_start).getUTCMonth() + 1) / 3)}º Trimestre`;
}

function referenciaDoSemestre(mes: MesDoCalendarV2): string {
  if (mes.cycle?.title_semester) return semPrefixoDeNivel(mes.cycle.title_semester);
  return `${mes.cycle?.title?.charAt(0)}º Semestre`;
}

function mesesDoCicloVigente(cycles: MesDoCalendarV2[]): MesDoCalendarV2[] {
  const vigente =
    cycles.find(mes => mes.cycle?.status === 'open') ?? [...cycles].sort((a, b) => b.cycle.date_start - a.cycle.date_start)[0];
  return cycles.filter(mes => mes.cycleId === vigente.cycleId);
}

function etapasDoMes(mes: MesDoCalendarV2): Etapa[] {
  const doMes = nomeDoMes(mes);
  const trimestre = referenciaDoTrimestre(mes);
  return [
    { tipo: 'cad', inicio: mes.date_start, fim: mes.date_finish, referencia: doMes },
    { tipo: 'val', inicio: mes.date_evaluation_step2_start, fim: mes.date_evaluation_step2_finish, referencia: doMes },
    { tipo: 'n1', inicio: mes.date_evaluation_step3_start, fim: mes.date_evaluation_step3_finish, referencia: doMes },
    { tipo: 'n2', inicio: mes.date_evaluation_step4_start, fim: mes.date_evaluation_step4_finish, referencia: trimestre },
    { tipo: 'pit', inicio: mes.date_evaluation_pitStop_start, fim: mes.date_evaluation_pitStop_finish, referencia: trimestre },
    {
      tipo: 'n3',
      inicio: mes.date_evaluation_step5_start,
      fim: mes.date_evaluation_step5_finish,
      referencia: referenciaDoSemestre(mes),
    },
  ];
}

/** Etapas do ciclo vigente, sem repetir as de trimestre e semestre, em ordem de início. */
export function etapasDoCiclo(cycles: CyclesInterface[] | undefined): Etapa[] {
  if (!cycles?.length) return [];

  const unicas = new Map<string, Etapa>();
  mesesDoCicloVigente(cycles as MesDoCalendarV2[])
    .flatMap(etapasDoMes)
    .filter(etapa => typeof etapa.inicio === 'number' && typeof etapa.fim === 'number')
    .forEach(etapa => unicas.set(`${etapa.tipo}:${etapa.inicio}:${etapa.fim}`, etapa));

  return Array.from(unicas.values()).sort(
    (a, b) => a.inicio - b.inicio || ORDEM_DOS_TIPOS.indexOf(a.tipo) - ORDEM_DOS_TIPOS.indexOf(b.tipo),
  );
}

const estaEmAndamento = (etapa: Etapa, agora: number) => etapa.inicio <= agora && agora <= etapa.fim;
const estaEncerrada = (etapa: Etapa, agora: number) => etapa.fim < agora;
const periodoDaEtapa = (etapa: Etapa) => `${diaEMes(etapa.inicio)} a ${diaEMes(etapa.fim)}`;

export function contarEncerradas(etapas: Etapa[], agora: number): number {
  return etapas.filter(etapa => estaEncerrada(etapa, agora)).length;
}

/** Etapas agrupadas pelo mês de início; as encerradas só entram com `mostrarEncerradas`. */
export function agruparPorMes(
  etapas: Etapa[],
  agora: number,
  mostrarEncerradas: boolean,
  legenda: ItemDaLegenda[] = LEGENDA_DO_CALENDARIO,
): MesDoCalendario[] {
  const grupos = new Map<string, MesDoCalendario>();
  const nomeECor = (tipo: TipoDeEtapa) => {
    const item = legenda.find(entrada => entrada.tipo === tipo) ?? { ...LEGENDA_POR_TIPO[tipo] };
    return { nome: item.nome, cor: item.cor };
  };

  etapas
    .filter(etapa => mostrarEncerradas || !estaEncerrada(etapa, agora))
    .forEach(etapa => {
      const inicio = emBrasilia(etapa.inicio);
      const chave = `${inicio.getUTCFullYear()}-${String(inicio.getUTCMonth() + 1).padStart(2, '0')}`;
      const grupo = grupos.get(chave) ?? { chave, nome: MESES[inicio.getUTCMonth()], ano: inicio.getUTCFullYear(), etapas: [] };
      grupos.set(chave, grupo);
      grupo.etapas.push({
        ...etapa,
        ...nomeECor(etapa.tipo),
        periodo: periodoDaEtapa(etapa),
        agora: estaEmAndamento(etapa, agora),
        encerrada: estaEncerrada(etapa, agora),
      });
    });

  return Array.from(grupos.values());
}

/** "Fase atual": a apreciação em andamento; sem ela, o cadastramento em andamento. */
export function rotuloFaseAtual(etapas: Etapa[], agora: number): string | null {
  const emAndamento = etapas.filter(etapa => estaEmAndamento(etapa, agora));
  const atual = emAndamento.find(etapa => etapa.tipo !== 'cad') ?? emAndamento[0];
  return atual ? `${LEGENDA_POR_TIPO[atual.tipo].nome} · ${atual.referencia}` : null;
}

export function textoDoPrazo(etapas: Etapa[], agora: number): string | null {
  const validacoes = etapas.filter(etapa => etapa.tipo === 'val');

  const emCurso = validacoes.find(etapa => estaEmAndamento(etapa, agora));
  if (emCurso) return `A validação desta fase vai de ${periodoDaEtapa(emCurso)}.`;

  const proxima = validacoes.find(etapa => etapa.inicio > agora);
  return proxima ? `A próxima validação começa em ${diaEMes(proxima.inicio)}.` : null;
}
